import { loadWrapper, PauseCover } from "aic";
import { useAppSettings, useHierarchyContext } from "contexts/AppSettingsContext";
import { eAccessRights, eHierarchyLevel } from "functions/api/enums";
import { HierarchyQueryParams } from "functions/api/hierarchy/HierarchyQueryParams";
import { HierarchyReadDto } from "functions/api/hierarchy/HierarchyReadDto";
import { loadHierarchy } from "functions/api/hierarchy/loadHierarchy";
import { IRequestStatus } from "functions/api/interface";
import { childHierarchyLevel } from "functions/childHierarchyLevel";
import { useEffect, useEffectEvent, useState, useTransition } from "react";
import { AddHierarchyButton } from "./AddHierarchyButton";
import { ChildLink } from "./ChildLink";

/**
 * Renders links to child hierarchies. Shows an add button when the current
 * user has permission to create child hierarchies.
 */
export const ChildLinks: React.FC = () => {
  const appSettings = useAppSettings();
  const hierarchyContext = useHierarchyContext();

  const [pending, startTransition] = useTransition();
  const [corpRequest, setCorpRequest] = useState<IRequestStatus<HierarchyQueryParams>>({
    requesting: false,
    error: false,
  });
  const [corp, setCorp] = useState<HierarchyReadDto | null>(null);

  const getCorporate = useEffectEvent(async () => {
    const request = await loadWrapper(
      loadHierarchy,
      { hierarchyName: "Corporate" } as HierarchyQueryParams,
      corpRequest,
      setCorpRequest,
      appSettings.backEndUrl,
    );
    /* istanbul ignore else */
    if (request?.success) {
      startTransition(() => setCorp(request.data));
    }
  });

  useEffect(() => {
    if (!hierarchyContext?.hierarchy) {
      getCorporate();
    }
  }, [hierarchyContext?.hierarchy]);

  const children =
    hierarchyContext && hierarchyContext.hierarchy
      ? hierarchyContext.hierarchy.children
      : corp?.children;

  const nlevel = hierarchyContext?.hierarchy
    ? childHierarchyLevel(hierarchyContext.hierarchy)
    : // istanbul ignore next
      eHierarchyLevel.TA;

  return hierarchyContext?.hierarchy?.level === eHierarchyLevel.Report ? null : (
    <div style={{ position: "relative" }}>
      {(corpRequest.requesting || pending) && <PauseCover />}
      {!children || children.length === 0 ? (
        <div>
          No
          {nlevel === eHierarchyLevel.Study ? " Studies" : ` ${nlevel}s`}
        </div>
      ) : (
        <>
          <h5>{nlevel}</h5>
          <div
            className="small-scrollbar"
            style={{ maxHeight: "400px" }}
          >
            {children
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((h, i) => (
                <ChildLink
                  key={i}
                  hierarchy={h}
                />
              ))}
          </div>
        </>
      )}
      {hierarchyContext?.accessRights.includes(eAccessRights.CreateHierarchy) && (
        <AddHierarchyButton key={hierarchyContext!.hierarchy?.id} />
      )}
      <hr />
    </div>
  );
};

ChildLinks.displayName = "ChildLinks";
